import React, { useState, useEffect } from 'react';
import { FileVideo, Book, FileText, Link, Clock, User, Edit, Trash2, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import { GoDeviceCameraVideo } from "react-icons/go";
import { FiBookOpen } from "react-icons/fi";
import useSuperAdminContentManagementStore from '@/stores/SuperAdminStore/SuperAdminContentManagementStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import LessonDetails from './LessonDetails/LessonDetails';
import SuperAdminEditLessonModal from './EditLessonModal';
import './LibraryTab.css';

const ITEMS_PER_PAGE = 12;

// Icon mapping based on content type
const getIconByType = (type: 'document' | 'scorm' | 'mp4' | 'url'): React.ReactNode => {
  const iconMap = {
    mp4: <GoDeviceCameraVideo size={48} />,
    scorm: <FiBookOpen size={48} />,
    document: <FileText size={48} />,
    url: <Link size={48} />
  };
  return iconMap[type] || <FileText size={48} />;
};

const LibraryTab: React.FC = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    standaloneLessons,
    loading,
    error,
    fetchStandaloneLessons,
    deleteLesson,
  } = useSuperAdminContentManagementStore();

  // Fetch standalone lessons on mount
  useEffect(() => {
    fetchStandaloneLessons();
  }, [fetchStandaloneLessons]);

  // Reset to page 1 when lessons change
  useEffect(() => {
    setCurrentPage(1);
  }, [standaloneLessons]);

  // Pagination calculations
  const totalLessons = standaloneLessons?.length || 0;
  const totalPages = Math.ceil(totalLessons / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentLessons = standaloneLessons?.slice(startIndex, endIndex) || [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewLesson = (lessonId: number) => {
    setSelectedLessonId(lessonId);
    setShowDetails(true);
  };

  const handleCloseDetails = () => {
    setShowDetails(false);
    setSelectedLessonId(null);
  };

  const handleEditLesson = (lessonId: number) => {
    setSelectedLessonId(lessonId);
    setShowDetails(false);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedLessonId(null);
  };

  const handleEditSuccess = () => {
    // Refresh the list so edited details show immediately.
    fetchStandaloneLessons();
  };

  const handleDeleteLesson = async (lessonId: number) => {
    const success = await deleteLesson(lessonId);
    if (success) {
      setShowDeleteConfirm(null);
      // Refresh the list after deletion
      fetchStandaloneLessons();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (showEditModal && selectedLessonId) {
    return (
      <SuperAdminEditLessonModal
        lessonId={selectedLessonId}
        onClose={handleCloseEditModal}
        onSuccess={handleEditSuccess}
      />
    );
  }

  if (showDetails && selectedLessonId) {
    return (
      <LessonDetails
        lessonId={selectedLessonId}
        onClose={handleCloseDetails}
        onEdit={handleEditLesson}
        onDelete={handleDeleteLesson}
      />
    );
  }

  if (loading && !standaloneLessons) {
    return (
      <div className="s-admin-library-container">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !standaloneLessons) {
    return (
      <div className="s-admin-library-container">
        <ErrorDisplay
          title="Failed to Load Lessons"
          message={error}
          type="error"
          showRetry
          onRetry={fetchStandaloneLessons}
        />
      </div>
    );
  }

  return (
    <div className="s-admin-library-container">
      {standaloneLessons && standaloneLessons.length === 0 ? (
        <div className="s-admin-library-empty-state">
          <FileText size={64} color="#9ca3af" />
          <h3>No Lessons Created Yet</h3>
          <p>Create standalone lessons from the Upload tab to see them here.</p>
        </div>
      ) : (
        <>
          <div className="s-admin-library-content-grid">
            {currentLessons.map((lesson) => (
              <div key={lesson.id} className="s-admin-library-content-card">
                <div className={`s-admin-library-status-badge s-admin-library-status-${
                  lesson.lessonType === 'ILTS' ? 'ilts' : 'active'
                }`}>
                  {lesson.lessonType}
                </div>

                <div className="s-admin-library-content-icon">
                  {lesson.contentType ? getIconByType(lesson.contentType as any) : <FileText size={48} />}
                </div>

                <h3 className="s-admin-library-content-title">{lesson.title}</h3>

                {lesson.contentType && (
                  <span className={`s-admin-library-type-badge s-admin-library-type-${lesson.contentType.toLowerCase()}`}>
                    {lesson.contentType.toUpperCase()}
                  </span>
                )}

                {lesson.description && (
                  <p className="s-admin-library-content-description">{lesson.description}</p>
                )}

                <div className="s-admin-library-content-meta">
                  {lesson.lessonDuration && (
                    <div className="s-admin-library-meta-item">
                      <Clock size={14} />
                      <span>{lesson.lessonDuration}</span>
                    </div>
                  )}
                  <div className="s-admin-library-meta-item">
                    <User size={14} />
                    <span>{formatDate(lesson.createdDate)}</span>
                  </div>
                </div>

                {lesson.iltsInfo && (
                  <div className="s-admin-library-ilts-info">
                    <span className="s-admin-library-ilts-badge">{lesson.iltsInfo.mode}</span>
                    {lesson.iltsInfo.startDate && (
                      <span className="s-admin-library-ilts-date">
                        {new Date(lesson.iltsInfo.startDate).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}

                <div className="s-admin-library-content-actions">
                  <button
                    className="s-admin-library-btn-view"
                    onClick={() => handleViewLesson(lesson.id)}
                    title="View Lesson"
                  >
                    <Eye size={16} />
                    View
                  </button>
                  <button
                    className="s-admin-library-btn-edit"
                    onClick={() => handleEditLesson(lesson.id)}
                    title="Edit Lesson"
                  >
                    <Edit size={16} />
                    Edit
                  </button>
                  <button
                    className="s-admin-library-btn-delete"
                    onClick={() => setShowDeleteConfirm(lesson.id)}
                    title="Delete Lesson"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </div>

                {/* Delete Confirmation */}
                {showDeleteConfirm === lesson.id && (
                  <div className="s-admin-library-delete-confirm">
                    <p>Delete this lesson?</p>
                    <div className="s-admin-library-delete-actions">
                      <button
                        onClick={() => handleDeleteLesson(lesson.id)}
                        className="s-admin-library-btn-confirm-delete"
                      >
                        Yes, Delete
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(null)}
                        className="s-admin-library-btn-cancel-delete"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="s-admin-library-pagination">
              <span className="s-admin-library-pagination-info">
                Showing {startIndex + 1}-{Math.min(endIndex, totalLessons)} of {totalLessons} lesson{totalLessons !== 1 ? 's' : ''}
              </span>

              <div className="s-admin-library-pagination-controls">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="s-admin-library-page-btn"
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  const showPage =
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1);

                  const showEllipsis =
                    (page === 2 && currentPage > 3) ||
                    (page === totalPages - 1 && currentPage < totalPages - 2);

                  if (showEllipsis) {
                    return <span key={page} className="s-admin-library-pagination-ellipsis">...</span>;
                  }

                  if (!showPage) {
                    return null;
                  }

                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`s-admin-library-page-btn ${
                        currentPage === page ? 's-admin-library-page-active' : ''
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="s-admin-library-page-btn"
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Show info even if only 1 page */}
          {totalPages === 1 && (
            <div className="s-admin-library-pagination">
              <span className="s-admin-library-pagination-info">
                Showing {totalLessons} lesson{totalLessons !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LibraryTab;
